import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import CheckCircle from '@mui/icons-material/esm/CheckCircle';
import CalendarMonth from '@mui/icons-material/esm/CalendarMonth';
import ExpandMore from '@mui/icons-material/esm/ExpandMore';
import Groups from '@mui/icons-material/esm/Groups';
import { RootState } from '../../store';
import teamService, { TeamListItem } from '../../services/api/teamService';
import timesheetService, { Timesheet, Worker } from '../../services/api/timesheetService';
import { Task } from '../../services/api/tasksService';
import SupervisorMonthCalendar from './SupervisorMonthCalendar';
import TaskPhotoEvidencePanel from './TaskPhotoEvidencePanel';
import { loadAllProjectTasks } from './supervisorFieldData';
import {
  completedTaskDate,
  dateInRange,
  presetDateRange,
  taskTeamIds,
  toLocalIsoDate,
} from './supervisorFieldUtils';

type TimesheetSection = 'checked' | 'plan';
type RangePreset = '1' | '2' | '3' | 'custom';

interface TeamDayGroup {
  team: TeamListItem;
  tasks: Task[];
}

const taskScheduleDate = (task: Task): string | null => task.startDate?.slice(0, 10) ?? task.dueDate?.slice(0, 10) ?? null;

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
};

const formatActualTime = (hours?: number | null): string => {
  if (!Number.isFinite(Number(hours))) return '-';
  const totalMinutes = Math.max(Number(hours) > 0 ? 1 : 0, Math.round(Number(hours) * 60));
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const wholeHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes ? `${wholeHours}h ${minutes}m` : `${wholeHours}h`;
};

export default function SupervisorTimesheetPage() {
  const { activeProjectId, projects, loading: projectsLoading } = useSelector((state: RootState) => state.projects);
  const effectiveProjectId = activeProjectId ?? projects[0]?.id ?? null;
  const today = useMemo(() => new Date(), []);
  const initialRange = useMemo(() => presetDateRange(1, today), [today]);
  const [section, setSection] = useState<TimesheetSection>('checked');
  const [rangePreset, setRangePreset] = useState<RangePreset>('1');
  const [from, setFrom] = useState(initialRange.from);
  const [to, setTo] = useState(initialRange.to);
  const [month, setMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState(toLocalIsoDate(today));
  const [tasks, setTasks] = useState<Task[]>([]);
  const [teams, setTeams] = useState<TeamListItem[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<TeamDayGroup | null>(null);
  const [selectedTimesheet, setSelectedTimesheet] = useState<Timesheet | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(false);
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const projectName = projects.find((project) => project.id === effectiveProjectId)?.name ?? 'Active project';

  const load = useCallback(async () => {
    if (!effectiveProjectId) return;
    setLoading(true);
    setError(null);
    try {
      const [taskRows, teamRows, workerRows, sheetRows] = await Promise.all([
        loadAllProjectTasks(effectiveProjectId),
        teamService.getTeams(),
        timesheetService.listWorkers({ active: true }),
        timesheetService.list({ projectId: effectiveProjectId, from, to }),
      ]);
      setTasks(taskRows);
      setTeams(teamRows.filter((team) => team.isActive !== false));
      setWorkers(workerRows.filter((worker) => worker.isActive));
      setTimesheets(sheetRows);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.response?.data?.error || 'Failed to load timesheet planning data.');
    } finally {
      setLoading(false);
    }
  }, [effectiveProjectId, from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const applyPreset = (value: RangePreset) => {
    setRangePreset(value);
    if (value !== 'custom') {
      const next = presetDateRange(Number(value), today);
      setFrom(next.from);
      setTo(next.to);
    }
  };

  const completedTasks = useMemo(
    () => tasks
      .filter((task) => dateInRange(completedTaskDate(task), from, to))
      .sort((a, b) => String(completedTaskDate(b)).localeCompare(String(completedTaskDate(a)))),
    [from, tasks, to]
  );

  const completedTasksByDate = useMemo(() => {
    const groups = new Map<string, Task[]>();
    completedTasks.forEach((task) => {
      const date = completedTaskDate(task) ?? 'No completion date';
      groups.set(date, [...(groups.get(date) ?? []), task]);
    });
    return [...groups.entries()].sort(([dateA], [dateB]) => dateB.localeCompare(dateA));
  }, [completedTasks]);

  const totalHours = useMemo(
    () => timesheets.reduce(
      (sum, sheet) => sum + (sheet.entries ?? []).reduce((entrySum, entry) => entrySum + Number(entry.hours || 0), 0),
      0
    ),
    [timesheets]
  );

  const groupsForDate = useCallback((date: string): TeamDayGroup[] => {
    const byTeam = new Map<string, Task[]>();
    tasks.filter((task) => taskScheduleDate(task) === date).forEach((task) => {
      taskTeamIds(task).forEach((teamId) => byTeam.set(teamId, [...(byTeam.get(teamId) ?? []), task]));
    });
    return [...byTeam.entries()]
      .map(([teamId, teamTasks]) => ({ team: teams.find((team) => team.id === teamId), tasks: teamTasks }))
      .filter((row): row is TeamDayGroup => Boolean(row.team))
      .sort((a, b) => a.team.name.localeCompare(b.team.name));
  }, [tasks, teams]);

  const reviewTimesheet = async (row: Timesheet, action: 'approve' | 'return') => {
    setReviewingId(row.id);
    setError(null);
    setNotice(null);
    try {
      if (action === 'approve') {
        await timesheetService.approve(row.id);
        setNotice('Timesheet approved.');
      } else {
        await timesheetService.return(row.id);
        setNotice('Timesheet returned for revision.');
      }
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.response?.data?.error || `Failed to ${action} the timesheet.`);
    } finally {
      setReviewingId(null);
    }
  };

  const timesheetHours = (sheet: Timesheet) =>
    (sheet.entries ?? []).reduce((sum, entry) => sum + Number(entry.hours || 0), 0);

  const teamNameFor = (teamId: string | null | undefined) =>
    teams.find((team) => team.id === teamId)?.name ?? 'Unassigned team';

  const assignedTeamNameFor = (task: Task | null | undefined): string => {
    if (!task) return 'Unassigned team';
    const activeAssignment = task.teamAssignments?.find((assignment) => {
      const status = String(assignment.lifecycleStatus ?? assignment.lifecycle_status ?? '').toLowerCase();
      return status !== 'cancelled' && status !== 'removed';
    });
    return activeAssignment?.team?.name
      ?? task.team?.name
      ?? (task.assignedTo ? `${task.assignedTo.firstName} ${task.assignedTo.lastName}`.trim() : '')
      ?? 'Unassigned team';
  };

  const completedByNameFor = useCallback((task: Task | null | undefined): string => {
    if (!task) return 'Worker not recorded';
    const taskWorkerNames = timesheets
      .flatMap((sheet) => sheet.entries ?? [])
      .filter((entry) => entry.taskId === task.id && entry.worker?.name)
      .map((entry) => entry.worker?.name as string);
    const uniqueTaskWorkerNames = [...new Set(taskWorkerNames)];
    return task.completedByName
      ?? task.completedBy?.name
      ?? task.completedBy?.email
      ?? (uniqueTaskWorkerNames.length ? uniqueTaskWorkerNames.join(', ') : 'Worker not recorded');
  }, [timesheets]);

  const timesheetTaskIds = useMemo(
    () => (selectedTimesheet?.entries ?? []).map((entry) => entry.taskId).filter((id): id is string => Boolean(id)),
    [selectedTimesheet]
  );

  const selectedDayGroups = useMemo(() => groupsForDate(selectedDate), [groupsForDate, selectedDate]);
  const selectedCrewNames = useMemo(() => {
    if (!selectedGroup) return [];
    const names = new Set<string>();
    selectedGroup.team.members?.forEach((member) => names.add(`${member.firstName} ${member.lastName}`.trim()));
    workers.filter((worker) => worker.teamId === selectedGroup.team.id).forEach((worker) => names.add(worker.name));
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [selectedGroup, workers]);

  if (!effectiveProjectId) {
    return projectsLoading
      ? <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
      : <Alert severity="warning">No project is available for timesheet planning.</Alert>;
  }

  return (
    <Stack spacing={2.5}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
        <Box>
          <Typography variant="h4" sx={{ fontSize: 28, fontWeight: 900 }}>Timesheet</Typography>
          <Typography color="text.secondary">{projectName} - completed work and team work plan</Typography>
        </Box>
        {loading && <Chip icon={<CircularProgress size={16} />} label="Refreshing" sx={{ alignSelf: 'flex-start' }} />}
      </Stack>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}

      <ToggleButtonGroup
        value={section}
        exclusive
        fullWidth
        onChange={(_, value: TimesheetSection | null) => value && setSection(value)}
        aria-label="Timesheet section"
        sx={{ maxWidth: 560 }}
      >
        <ToggleButton value="checked"><CheckCircle sx={{ mr: 1 }} />Checked Off</ToggleButton>
        <ToggleButton value="plan"><CalendarMonth sx={{ mr: 1 }} />Work Plan</ToggleButton>
      </ToggleButtonGroup>

      {section === 'checked' ? (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
              <TextField
                select
                label="Time frame"
                value={rangePreset}
                onChange={(event) => applyPreset(event.target.value as RangePreset)}
                sx={{ minWidth: 190 }}
              >
                <MenuItem value="1">Previous month</MenuItem>
                <MenuItem value="2">Previous 2 months</MenuItem>
                <MenuItem value="3">Previous 3 months</MenuItem>
                <MenuItem value="custom">Custom range</MenuItem>
              </TextField>
              <TextField
                type="date"
                label="From"
                InputLabelProps={{ shrink: true }}
                value={from}
                onChange={(event) => { setRangePreset('custom'); setFrom(event.target.value); }}
              />
              <TextField
                type="date"
                label="To"
                InputLabelProps={{ shrink: true }}
                value={to}
                onChange={(event) => { setRangePreset('custom'); setTo(event.target.value); }}
              />
              <Stack direction="row" spacing={1} sx={{ ml: { md: 'auto' } }}>
                <Chip label={`${completedTasks.length} completed`} color="success" />
                <Chip label={`${totalHours.toFixed(1)} hours`} />
              </Stack>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
            {completedTasks.length === 0 ? (
              <Alert severity="info">No completed tasks fall within this time frame.</Alert>
            ) : completedTasksByDate.map(([date, dateTasks], groupIndex) => (
              <Accordion key={date} defaultExpanded={groupIndex === 0} disableGutters>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                    <Typography sx={{ fontWeight: 900 }}>{date}</Typography>
                    <Chip size="small" color="success" label={`${dateTasks.length} completed`} />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  {dateTasks.map((task, index) => (
                    <Box key={task.id} sx={{ p: 1.75, borderTop: index === 0 ? '1px solid #e3e6e3' : 0, borderBottom: index === dateTasks.length - 1 ? 0 : '1px solid #e3e6e3' }}>
                      <Grid container spacing={1} alignItems="center">
                        <Grid item xs={12} sm={6}>
                          <Typography sx={{ fontWeight: 800 }}>{task.title}</Typography>
                          <Typography variant="body2" color="text.secondary">{task.workLocationRef?.name || task.workLocation || 'Site'}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={2}>
                          <Typography variant="caption" color="text.secondary">Assigned to</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 800 }}>{assignedTeamNameFor(task)}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={2}>
                          <Typography variant="caption" color="text.secondary">Completed by</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 800 }}>{completedByNameFor(task)}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={2} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                          <Stack direction={{ xs: 'row', sm: 'column' }} spacing={0.75} alignItems={{ xs: 'center', sm: 'flex-end' }}>
                            <Chip size="small" color="success" label="Done" />
                            <Typography variant="caption" color="text.secondary">{formatActualTime(task.actualHours)}</Typography>
                            <Button size="small" variant="outlined" onClick={() => setSelectedTask(task)}>Info</Button>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            ))}
          </Paper>

          <Box component="section">
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
              <CheckCircle color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 900 }}>Team timesheets</Typography>
              <Chip size="small" label={timesheets.length} />
            </Stack>
            <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
              {timesheets.length === 0 ? (
                <Alert severity="info">No timesheets have been recorded in this time frame.</Alert>
              ) : timesheets.map((sheet, index) => (
                <Box key={sheet.id} sx={{ p: 1.75, borderBottom: index === timesheets.length - 1 ? 0 : '1px solid #e3e6e3' }}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} sm={2}>
                      <Typography sx={{ fontWeight: 900 }}>{sheet.workDate?.slice(0, 10)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography sx={{ fontWeight: 800 }}>{teamNameFor(sheet.teamId)}</Typography>
                      <Typography variant="body2" color="text.secondary">{(sheet.entries ?? []).length} entries - {timesheetHours(sheet).toFixed(1)} hours</Typography>
                    </Grid>
                    <Grid item xs={4} sm={2}>
                      <Chip
                        size="small"
                        label={sheet.status}
                        color={sheet.status === 'approved' ? 'success' : sheet.status === 'submitted' ? 'primary' : sheet.status === 'returned' ? 'warning' : 'default'}
                      />
                    </Grid>
                    <Grid item xs={8} sm={4} sx={{ textAlign: 'right' }}>
                      <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setSelectedTimesheet(sheet)}
                        >
                          View details
                        </Button>
                        {sheet.status === 'submitted' && (
                          <>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            disabled={reviewingId === sheet.id}
                            onClick={() => reviewTimesheet(sheet, 'approve')}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            disabled={reviewingId === sheet.id}
                            onClick={() => reviewTimesheet(sheet, 'return')}
                          >
                            Return
                          </Button>
                          </>
                        )}
                      </Stack>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Paper>
          </Box>
        </Stack>
      ) : (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: { xs: 1, sm: 2 }, borderRadius: 1 }}>
            <SupervisorMonthCalendar
              month={month}
              selectedDate={selectedDate}
              onMonthChange={(nextMonth) => {
                setMonth(nextMonth);
                setSelectedDate(toLocalIsoDate(new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 1)));
              }}
              onSelectDate={setSelectedDate}
              renderDay={(date) => (
                <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                  {groupsForDate(date).slice(0, 2).map((group) => (
                    <Typography key={group.team.id} noWrap sx={{ fontSize: 10, fontWeight: 700 }}>
                      {group.team.name}: {group.tasks.length}
                    </Typography>
                  ))}
                  {groupsForDate(date).length > 2 && <Typography sx={{ fontSize: 10 }}>+{groupsForDate(date).length - 2} teams</Typography>}
                </Stack>
              )}
            />
          </Paper>

          <Box component="section">
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
              <Groups />
              <Typography variant="h6" sx={{ fontWeight: 900 }}>{selectedDate} team plan</Typography>
            </Stack>
            {selectedDayGroups.length === 0 ? (
              <Alert severity="info">No team tasks are scheduled to start on this date.</Alert>
            ) : (
              <Grid container spacing={1.5}>
                {selectedDayGroups.map((group) => (
                  <Grid item xs={12} sm={6} lg={4} key={group.team.id}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => setSelectedGroup(group)}
                      sx={{ minHeight: 76, justifyContent: 'space-between', px: 2 }}
                    >
                      <Box sx={{ textAlign: 'left', minWidth: 0 }}>
                        <Typography noWrap sx={{ fontWeight: 900 }}>{group.team.name}</Typography>
                        <Typography variant="body2" color="text.secondary">Open crew and tasks</Typography>
                      </Box>
                      <Chip label={group.tasks.length} color="primary" />
                    </Button>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Stack>
      )}

      <Dialog open={Boolean(selectedGroup)} onClose={() => setSelectedGroup(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedGroup?.team.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Box>
              <Typography sx={{ fontWeight: 900, mb: 1 }}>Crew members</Typography>
              {selectedCrewNames.length === 0 ? (
                <Alert severity="info">No active crew members are linked to this team.</Alert>
              ) : (
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {selectedCrewNames.map((name) => <Chip key={name} label={name} />)}
                </Stack>
              )}
            </Box>
            <Divider />
            <Box>
              <Typography sx={{ fontWeight: 900, mb: 1 }}>Tasks for {selectedDate}</Typography>
              <Stack spacing={1}>
                {selectedGroup?.tasks.map((task) => (
                  <Box key={task.id} sx={{ borderLeft: '3px solid #2f855a', pl: 1.25, py: 0.5 }}>
                    <Typography sx={{ fontWeight: 800 }}>{task.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{task.workLocationRef?.name || task.workLocation || 'Site'}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setSelectedGroup(null)}>Close</Button></DialogActions>
      </Dialog>

      <Dialog open={Boolean(selectedTimesheet)} onClose={() => setSelectedTimesheet(null)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedTimesheet?.workDate?.slice(0, 10)} Timesheet Details</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Crew</Typography>
                  <Typography sx={{ fontWeight: 900 }}>{teamNameFor(selectedTimesheet?.teamId)}</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Typography sx={{ fontWeight: 900 }}>{selectedTimesheet?.status ?? '-'}</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary">Hours</Typography>
                  <Typography sx={{ fontWeight: 900 }}>{selectedTimesheet ? timesheetHours(selectedTimesheet).toFixed(1) : '0.0'}</Typography>
                </Grid>
              </Grid>
            </Paper>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Worker</TableCell>
                  <TableCell>Task</TableCell>
                  <TableCell>Hours</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(selectedTimesheet?.entries ?? []).map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>{entry.worker?.name ?? entry.workerId}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 800 }}>{entry.task?.name ?? entry.taskId ?? '-'}</Typography>
                      {entry.task?.taskCode && <Typography variant="caption" color="text.secondary">{entry.task.taskCode}</Typography>}
                    </TableCell>
                    <TableCell>{Number(entry.hours || 0).toFixed(2)}</TableCell>
                    <TableCell>{entry.notes ?? '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <TaskPhotoEvidencePanel
              projectId={effectiveProjectId}
              taskIds={timesheetTaskIds}
              title="Photos linked to this timesheet's tasks"
            />
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setSelectedTimesheet(null)}>Close</Button></DialogActions>
      </Dialog>

      <Dialog open={Boolean(selectedTask)} onClose={() => setSelectedTask(null)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedTask?.title ?? 'Task details'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Task code</Typography>
                  <Typography sx={{ fontWeight: 900 }}>{selectedTask?.taskCode ?? '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Assigned to</Typography>
                  <Typography sx={{ fontWeight: 900 }}>{assignedTeamNameFor(selectedTask)}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Completed</Typography>
                  <Typography sx={{ fontWeight: 900 }}>{selectedTask ? completedTaskDate(selectedTask) ?? '-' : '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Completed by</Typography>
                  <Typography sx={{ fontWeight: 900 }}>{completedByNameFor(selectedTask)}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Started</Typography>
                  <Typography sx={{ fontWeight: 900 }}>{formatDateTime(selectedTask?.actualStartDate)}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Finished</Typography>
                  <Typography sx={{ fontWeight: 900 }}>{formatDateTime(selectedTask?.completionDate)}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Actual time</Typography>
                  <Typography sx={{ fontWeight: 900 }}>{formatActualTime(selectedTask?.actualHours)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Location</Typography>
                  <Typography sx={{ fontWeight: 900 }}>{selectedTask?.workLocationRef?.name || selectedTask?.workLocation || 'Site'}</Typography>
                </Grid>
              </Grid>
            </Paper>
            <TaskPhotoEvidencePanel
              projectId={selectedTask?.projectId}
              taskIds={[selectedTask?.id]}
              title="Photos linked to this completed task"
            />
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setSelectedTask(null)}>Close</Button></DialogActions>
      </Dialog>
    </Stack>
  );
}
